import React from "react";
import { useAsyncEffect, useForceUpdate } from "../hooks";
import { upperFirst, isNil, debounce } from "lodash";
import { HEADOFFICE_OPTION_VALUE } from "constants/AppConstants";
import { FlaskReq } from "helpers/Request";
import { FlaskErrorCode, WINDOW_EVENT } from "helpers/helper";
import { TicketSchedulingUserSettingsData } from "./usersettings";

export enum UserSettingId {
  selectedMenu = "selectedMenu",
  IsSidebarCollpased = "isSidebarCollapsed",
  TicketScheduling = "ticketScheduling",
  WorkOrderScheduling = "workOrderScheduling",
  WorkOrderList = "workOrderList",
  DashboardCalendar = "dashboardCalendar"
}

const LOCAL_STORAGE_PREFIX = "flSetting";

export class UserSetting<T = any> {
  readonly id: string;
  readonly localStorageId: string;
  private didFetch: boolean;

  constructor(id: UserSettingId) {
    this.id = id;
    this.localStorageId = `${LOCAL_STORAGE_PREFIX}${upperFirst(id)}`;
    this.didFetch = this.localStorageId in localStorage;

    window.addEventListener(WINDOW_EVENT.LOGOUT, () => {
      this.cleanUp();
    });
  }

  private cleanUp = debounce(() => {
    this.didFetch = false;
    localStorage.removeItem(this.localStorageId);
  }, 200);

  private mapTicketSchedulingSetting = data => ({
    ...data,
    driversSelected: (
      data as TicketSchedulingUserSettingsData
    ).driversSelected.map(driver => ({
      ...driver,
      label: driver.name,
      value: driver.srm_uid || HEADOFFICE_OPTION_VALUE
    }))
  });

  private async fetch() {
    let {
      data: { content }
    } = await FlaskReq(
      {
        method: "get",
        url: `${localStorage.flask_url}/view_settings/users/${this.id}`
      },
      {
        showMessage: false
      }
    );
    if (this.id === UserSettingId.TicketScheduling) {
      let parsedContent = JSON.parse(content);
      content = JSON.stringify(this.mapTicketSchedulingSetting(parsedContent));
    }

    localStorage.setItem(this.localStorageId, content);
  }

  private async post(content: T) {
    await FlaskReq(
      {
        method: "post",
        url: `${localStorage.flask_url}/view_settings/users`,
        data: {
          id: this.id,
          content: JSON.stringify(content)
        }
      },
      {
        showMessage: false
      }
    );
  }

  private async put(content: T) {
    await FlaskReq(
      {
        method: "put",
        url: `${localStorage.flask_url}/view_settings/users/${this.id}`,
        data: {
          content: JSON.stringify(content)
        }
      },
      {
        showMessage: false
      }
    );
  }

  Provider: React.FC = props => {
    const [, forceUpdate] = useForceUpdate();

    useAsyncEffect(async () => {
      if (this.didFetch) return;

      try {
        await this.fetch();
      } finally {
        this.didFetch = true;
        forceUpdate();
      }
    }, []);

    if (!this.didFetch) return null;

    return props.children as any;
  };

  withProvider<C extends React.ComponentType = any, P = React.Props<C>>(
    WrappedComponent: C
  ): React.FC<P> {
    return (props: P) => {
      return (
        <this.Provider>
          <WrappedComponent {...(props as any)} />
        </this.Provider>
      );
    };
  }

  save = async (content: T) => {
    try {
      await this.put(content);
    } catch ({ response }) {
      if (response?.data?.errorCode === FlaskErrorCode.INVALID_RESOURCE_ID) {
        await this.post(content);
      }
    }

    if (this.id === UserSettingId.TicketScheduling) {
      content = this.mapTicketSchedulingSetting(content);
    }
    localStorage.setItem(this.localStorageId, JSON.stringify(content));
  };

  get = (): T => {
    const content = localStorage.getItem(this.localStorageId);

    if (isNil(content)) return undefined;
    try {
      return JSON.parse(content);
    } catch (e) {
      console.error(e);
      return undefined;
    }
  };
}
